/**
 * DepartmentsSection — the company org tree (ADR-0030).
 *
 * Renders the flat department list as a two-level row list: top-level units as
 * icon-tile rows, sub-units hung off a connector rail (no ASCII glyphs). Create
 * (top-level + inline "add sub-team"), inline rename, and soft-delete (a parent
 * with sub-teams is refused server-side). Server-gated by `settings.manage`;
 * `canManage` hides the affordances.
 */
import { useMemo, useState, type ReactNode } from "react";
import {
  Alert,
  Button,
  ConfirmDialog,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from "@flint/ui";
import {
  Building2,
  Check,
  ChevronDown,
  ChevronRight,
  Network,
  Pencil,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { trpc } from "../../../lib/trpc.ts";

interface Dept {
  id: string;
  name: string;
  parentDepartmentId: string | null;
  sort: number;
}

/** `null` = top-level add open · `string` = add a sub-unit under that id · `undefined` = closed. */
type AddTarget = string | null | undefined;

export interface DepartmentsSectionProps {
  canManage: boolean;
}

export function DepartmentsSection({ canManage }: DepartmentsSectionProps) {
  const q = trpc.departments.list.useQuery({});
  const utils = trpc.useUtils();

  const [addTarget, setAddTarget] = useState<AddTarget>(undefined);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState("");
  const [confirmDelete, setConfirmDelete] = useState<Dept | null>(null);
  // Collapsed top-level ids — default expanded; the set tracks what's folded.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const toggleCollapsed = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const expand = (id: string) =>
    setCollapsed((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });

  const refresh = () => utils.departments.list.invalidate();
  const create = trpc.departments.create.useMutation({
    onSuccess: async () => {
      await refresh();
      setAddTarget(undefined);
      toast.success("Department added");
    },
    onError: (e) => toast.error("Couldn't add department", { description: e.message }),
  });
  const update = trpc.departments.update.useMutation({
    onSuccess: async () => {
      await refresh();
      setEditingId(null);
      toast.success("Department renamed");
    },
    onError: (e) => toast.error("Couldn't rename department", { description: e.message }),
  });
  const archive = trpc.departments.archive.useMutation({
    onSuccess: async () => {
      await refresh();
      setConfirmDelete(null);
      toast.success("Department deleted");
    },
    onError: (e) => toast.error("Couldn't delete department", { description: e.message }),
  });

  // Flat list → { top, children }[] tree. Orphans (parent archived) surface as tops.
  const tree = useMemo(() => {
    const all = (q.data?.departments ?? []) as Dept[];
    const bySort = (a: Dept, b: Dept) => a.sort - b.sort || a.name.localeCompare(b.name);
    const topIds = new Set(all.filter((d) => !d.parentDepartmentId).map((d) => d.id));
    const tops = all.filter((d) => !d.parentDepartmentId || !topIds.has(d.parentDepartmentId));
    return tops.sort(bySort).map((top) => ({
      top,
      children: all.filter((d) => d.parentDepartmentId === top.id).sort(bySort),
    }));
  }, [q.data]);

  const topDepts = (q.data?.departments ?? []).filter((d) => !d.parentDepartmentId);
  const total = q.data?.departments.length ?? 0;
  const isEmpty = !q.isLoading && !q.error && total === 0;

  const submitRename = (dept: Dept) => {
    const next = draftName.trim();
    if (next && next !== dept.name) update.mutate({ departmentId: dept.id, name: next });
    else setEditingId(null);
  };
  const startEdit = (dept: Dept) => {
    setEditingId(dept.id);
    setDraftName(dept.name);
  };

  return (
    <section className="overflow-hidden rounded-md border border-line bg-paper shadow-sm">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-ink">Departments</h3>
            {total > 0 ? (
              <span className="rounded-full bg-bg px-1.5 py-0.5 text-xs font-medium tabular-nums text-muted">
                {total}
              </span>
            ) : null}
          </div>
          <p className="mt-0.5 text-xs text-muted">
            Your org structure — group teammates by function and nest sub-teams under a parent.
          </p>
        </div>
        {canManage ? (
          <Button
            intent="ghost"
            className="border border-line bg-paper"
            onClick={() => setAddTarget((t) => (t === null ? undefined : null))}
          >
            <Plus size={15} />
            New department
          </Button>
        ) : null}
      </header>

      {q.isLoading ? (
        <div className="space-y-2 p-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="h-8 w-8 animate-pulse rounded-md bg-bg" />
              <div className="h-3.5 w-40 animate-pulse rounded bg-bg" />
            </div>
          ))}
        </div>
      ) : q.error ? (
        <div className="p-4">
          <Alert intent="bad" title="Couldn't load departments" description={q.error.message} />
        </div>
      ) : isEmpty && addTarget === undefined ? (
        <EmptyState canManage={canManage} onAdd={() => setAddTarget(null)} />
      ) : (
        <div className="divide-y divide-line">
          {tree.map(({ top, children }) => {
            const addingHere = addTarget === top.id;
            const isExpanded = !collapsed.has(top.id) || addingHere;
            return (
              <div key={top.id} className="px-2 py-1.5">
                <DeptRow
                  dept={top}
                  childCount={children.length}
                  expanded={isExpanded}
                  onToggle={() => toggleCollapsed(top.id)}
                  canManage={canManage}
                  isEditing={editingId === top.id}
                  draftName={draftName}
                  onDraftChange={setDraftName}
                  onStartEdit={() => startEdit(top)}
                  onSubmitEdit={() => submitRename(top)}
                  onCancelEdit={() => setEditingId(null)}
                  onAddChild={() => {
                    expand(top.id);
                    setAddTarget(top.id);
                    setDraftName("");
                  }}
                  onDelete={() => setConfirmDelete(top)}
                  saving={update.isPending}
                />

                {(children.length > 0 || addingHere) && isExpanded ? (
                  <ul className="mt-0.5 pl-1">
                    {children.map((child, i) => {
                      const isLast = i === children.length - 1 && !addingHere;
                      return (
                        <ChildRow
                          key={child.id}
                          dept={child}
                          isLast={isLast}
                          canManage={canManage}
                          isEditing={editingId === child.id}
                          draftName={draftName}
                          onDraftChange={setDraftName}
                          onStartEdit={() => startEdit(child)}
                          onSubmitEdit={() => submitRename(child)}
                          onCancelEdit={() => setEditingId(null)}
                          onDelete={() => setConfirmDelete(child)}
                          saving={update.isPending}
                        />
                      );
                    })}
                    {addingHere ? (
                      <li className="flex items-stretch">
                        <Connector isLast />
                        <form
                          className="flex flex-1 items-center gap-1.5 py-1"
                          onSubmit={(e) => {
                            e.preventDefault();
                            const name = draftName.trim();
                            if (name) create.mutate({ name, parentDepartmentId: top.id });
                          }}
                        >
                          <Input
                            value={draftName}
                            onChange={(e) => setDraftName(e.currentTarget.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Escape") setAddTarget(undefined);
                            }}
                            placeholder={`Sub-team under ${top.name}`}
                            className="h-8 max-w-xs text-sm"
                            autoFocus
                          />
                          <SaveCancel
                            saving={create.isPending}
                            onCancel={() => setAddTarget(undefined)}
                          />
                        </form>
                      </li>
                    ) : null}
                  </ul>
                ) : null}
              </div>
            );
          })}

          {addTarget === null ? (
            <div className="bg-raised/50 px-3 py-3">
              <InlineAdd
                placeholder="Department name (e.g. Production)"
                pending={create.isPending}
                parentPicker={
                  topDepts.length > 0
                    ? { options: topDepts.map((d) => ({ id: d.id, name: d.name })) }
                    : undefined
                }
                onCancel={() => setAddTarget(undefined)}
                onSubmit={(name, parentId) =>
                  create.mutate({ name, parentDepartmentId: parentId ?? null })
                }
              />
            </div>
          ) : null}
        </div>
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(open) => {
          if (!open) setConfirmDelete(null);
        }}
        title="Delete department"
        description={
          confirmDelete
            ? `Delete "${confirmDelete.name}"? Roles and members pointing at it fall back to no department. This can't be undone.`
            : ""
        }
        intent="danger"
        confirmLabel="Delete"
        onConfirm={() => {
          if (confirmDelete) archive.mutate({ departmentId: confirmDelete.id });
        }}
      />
    </section>
  );
}

// ── Top-level row ────────────────────────────────────────────────────────────

interface RowProps {
  dept: Dept;
  canManage: boolean;
  isEditing: boolean;
  draftName: string;
  onDraftChange: (v: string) => void;
  onStartEdit: () => void;
  onSubmitEdit: () => void;
  onCancelEdit: () => void;
  onDelete: () => void;
  saving: boolean;
}

function DeptRow({
  dept,
  childCount,
  expanded,
  onToggle,
  onAddChild,
  ...row
}: RowProps & {
  childCount: number;
  expanded: boolean;
  onToggle: () => void;
  onAddChild: () => void;
}) {
  const hasChildren = childCount > 0;
  return (
    <div className="group/dept flex items-center gap-2 rounded-lg px-2 py-2 transition-colors hover:bg-raised">
      {hasChildren ? (
        <button
          type="button"
          onClick={onToggle}
          aria-label={expanded ? `Collapse ${dept.name}` : `Expand ${dept.name}`}
          aria-expanded={expanded}
          className="grid h-6 w-6 shrink-0 place-items-center rounded text-muted transition hover:bg-bg hover:text-ink-2"
        >
          {expanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        </button>
      ) : (
        <span aria-hidden className="h-6 w-6 shrink-0" />
      )}
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-sky-50 text-sky-700">
        <Building2 size={15} strokeWidth={1.75} aria-hidden />
      </span>

      {row.isEditing ? (
        <RenameForm {...row} />
      ) : (
        <>
          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate text-sm font-medium text-ink">{dept.name}</span>
            {childCount > 0 ? (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-bg px-1.5 py-0.5 text-xs font-medium text-muted"
                title={`${childCount} sub-${childCount === 1 ? "team" : "teams"}`}
              >
                <Network size={10} aria-hidden />
                {childCount}
              </span>
            ) : null}
          </div>
          {row.canManage ? (
            <div className="ml-auto flex items-center gap-0.5 opacity-0 transition focus-within:opacity-100 group-hover/dept:opacity-100">
              <IconButton label={`Add sub-team under ${dept.name}`} onClick={onAddChild}>
                <Plus size={14} />
              </IconButton>
              <IconButton label={`Rename ${dept.name}`} onClick={row.onStartEdit}>
                <Pencil size={13} />
              </IconButton>
              <IconButton label={`Delete ${dept.name}`} tone="danger" onClick={row.onDelete}>
                <Trash2 size={13} />
              </IconButton>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

// ── Child row (hung off the connector rail) ─────────────────────────────────

function ChildRow({ dept, isLast, ...row }: RowProps & { isLast: boolean }) {
  return (
    <li className="group/child flex items-stretch">
      <Connector isLast={isLast} />
      <div className="flex flex-1 items-center gap-2.5 rounded-md py-1.5 pr-2 transition-colors hover:bg-raised">
        <span
          aria-hidden
          className="h-1.5 w-1.5 shrink-0 rounded-full border border-line-2 bg-paper"
        />
        {row.isEditing ? (
          <RenameForm {...row} />
        ) : (
          <>
            <span className="truncate text-sm text-ink-2">{dept.name}</span>
            {row.canManage ? (
              <div className="ml-auto flex items-center gap-0.5 opacity-0 transition focus-within:opacity-100 group-hover/child:opacity-100">
                <IconButton label={`Rename ${dept.name}`} onClick={row.onStartEdit}>
                  <Pencil size={12} />
                </IconButton>
                <IconButton label={`Delete ${dept.name}`} tone="danger" onClick={row.onDelete}>
                  <Trash2 size={12} />
                </IconButton>
              </div>
            ) : null}
          </>
        )}
      </div>
    </li>
  );
}

/** The L-shaped rail segment that connects a child to its parent's vertical line. */
function Connector({ isLast }: { isLast: boolean }) {
  return (
    <span aria-hidden className="relative w-8 shrink-0">
      <span className={`absolute left-4 w-px bg-line ${isLast ? "top-0 h-1/2" : "inset-y-0"}`} />
      <span className="absolute left-4 top-1/2 h-px w-4 bg-line" />
    </span>
  );
}

// ── Shared inline bits ───────────────────────────────────────────────────────

function RenameForm({
  draftName,
  onDraftChange,
  onSubmitEdit,
  onCancelEdit,
  saving,
}: Pick<RowProps, "draftName" | "onDraftChange" | "onSubmitEdit" | "onCancelEdit" | "saving">) {
  return (
    <form
      className="flex flex-1 items-center gap-1.5"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmitEdit();
      }}
    >
      <Input
        value={draftName}
        onChange={(e) => onDraftChange(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") onCancelEdit();
        }}
        className="h-8 max-w-xs text-sm"
        autoFocus
      />
      <SaveCancel saving={saving} onCancel={onCancelEdit} />
    </form>
  );
}

function SaveCancel({ saving, onCancel }: { saving: boolean; onCancel: () => void }) {
  return (
    <>
      <button
        type="submit"
        disabled={saving}
        className="rounded p-1.5 text-ok transition hover:bg-ok-50 disabled:opacity-50"
        aria-label="Save"
      >
        <Check size={14} />
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="rounded p-1.5 text-muted transition hover:bg-bg"
        aria-label="Cancel"
      >
        <X size={14} />
      </button>
    </>
  );
}

interface InlineAddProps {
  placeholder: string;
  pending: boolean;
  parentPicker?: { options: Array<{ id: string; name: string }> } | undefined;
  onCancel: () => void;
  onSubmit: (name: string, parentId: string | null) => void;
}

const NO_PARENT = "__none__";

function InlineAdd({ placeholder, pending, parentPicker, onCancel, onSubmit }: InlineAddProps) {
  const [name, setName] = useState("");
  const [parent, setParent] = useState(NO_PARENT);
  return (
    <form
      className="flex flex-wrap items-center gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim()) onSubmit(name.trim(), parent === NO_PARENT ? null : parent);
      }}
    >
      <Input
        value={name}
        onChange={(e) => setName(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") onCancel();
        }}
        placeholder={placeholder}
        className="h-9 max-w-xs flex-1 text-sm"
        autoFocus
      />
      {parentPicker ? (
        <Select value={parent} onValueChange={setParent}>
          <SelectTrigger className="h-9 w-44 text-sm">
            <SelectValue placeholder="Top level" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NO_PARENT}>Top level</SelectItem>
            {parentPicker.options.map((d) => (
              <SelectItem key={d.id} value={d.id}>
                Under {d.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : null}
      <Button type="submit" disabled={!name.trim() || pending}>
        {pending ? "Adding…" : "Add"}
      </Button>
      <Button type="button" intent="ghost" onClick={onCancel} disabled={pending}>
        Cancel
      </Button>
    </form>
  );
}

function IconButton({
  label,
  onClick,
  children,
  tone = "default",
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
  tone?: "default" | "danger";
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={
        tone === "danger"
          ? "rounded p-1.5 text-muted transition hover:bg-bad-50 hover:text-bad"
          : "rounded p-1.5 text-muted transition hover:bg-bg hover:text-ink-2"
      }
    >
      {children}
    </button>
  );
}

function EmptyState({ canManage, onAdd }: { canManage: boolean; onAdd: () => void }) {
  return (
    <div className="flex flex-col items-center gap-3 px-4 py-10 text-center">
      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-sky-50 text-sky-700">
        <Building2 size={20} strokeWidth={1.75} aria-hidden />
      </span>
      <div>
        <p className="text-sm font-medium text-ink">No departments yet</p>
        <p className="mt-0.5 text-xs text-muted">
          Group teammates by function — create a top-level unit, then nest sub-teams under it.
        </p>
      </div>
      {canManage ? (
        <Button intent="ghost" className="border border-line bg-paper" onClick={onAdd}>
          <Plus size={15} />
          New department
        </Button>
      ) : null}
    </div>
  );
}
